import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navigation, Map as MapIcon, ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { createMap, tealPin, L } from "../lib/leafletMap";

/**
 * Compact map embed for Dashboard / Customers etc.
 * Wave 25.17 — switched from Google Maps to Leaflet + OSM tiles.
 */
export default function MapEmbed({ height = 320, showLink = true, customerId = null }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sites");
        const list = customerId ? data.filter((s) => s.customer_id === customerId) : data;
        setSites(list);
      } catch (e) {
        setError("Could not load sites.");
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
  }, [customerId]);

  useEffect(() => {
    if (!sites.length || !mapEl.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const { map } = createMap(mapEl.current, { center: [-33.92, 18.42], zoom: 11 });
    mapRef.current = map;
    const markers = [];
    let cancelled = false;

    (async () => {
      for (const s of sites) {
        if (cancelled) return;
        let pos = null;
        if (s.lat && s.lng) pos = { lat: s.lat, lng: s.lng };
        else if (s.id && s.address) {
          try {
            const { data } = await api.post(`/sites/${s.id}/resolve-coords`);
            if (typeof data?.lat === "number" && typeof data?.lng === "number") {
              pos = { lat: data.lat, lng: data.lng };
            }
          } catch {
            /* server logged */
          }
        }
        if (!pos) continue;
        const marker = L.marker([pos.lat, pos.lng], { icon: tealPin(), title: s.name }).addTo(map);
        marker.bindTooltip(s.name, { direction: "top", offset: [0, -28] });
        markers.push(marker);
      }
      if (markers.length > 0) {
        const group = L.featureGroup(markers);
        map.fitBounds(group.getBounds().pad(0.2));
        if (markers.length === 1) map.setZoom(13);
      }
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [sites]);

  return (
    <div className="hv-card overflow-hidden relative" data-testid="map-embed">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <MapIcon className="w-4 h-4 text-blue-400" />
          <div className="text-xs uppercase tracking-wider text-[#647087]">Site map</div>
          <div className="text-xs text-[#a0aabf]">· {sites.length} {sites.length === 1 ? "site" : "sites"}</div>
        </div>
        {showLink && (
          <Link
            to="/map"
            className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1"
            data-testid="map-embed-open"
          >
            Full map <ArrowUpRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="relative" style={{ height }}>
        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 text-xs text-rose-300 p-4 text-center">
            {error}
          </div>
        )}
        {sites.length === 0 && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#647087]">
            No sites with addresses to plot.
          </div>
        )}
        <div ref={mapEl} className="w-full h-full" data-testid="map-embed-canvas" />
      </div>
    </div>
  );
}

// re-export Navigation so consumers can show "Go" link if they want
export { Navigation };
